// The one "working on it" treatment, shared by every network-bound wait in the flow: session start,
// the pause between questions while the selector runs (question-flow.ts), and the match wait while
// the ranking call runs (result-screen.ts). The host's photo sits inside a ring that spins, with an
// optional list of steps beneath it for the longer match wait.

import { hostImageUrl } from "./assets";

export interface LoadingOptions {
  /** Accessible name announced for the whole block. */
  label: string;
  heading: string;
  /** A single quiet line under the heading, for waits too short to need steps. */
  note?: string;
  /** Progress steps, first one active. Advance them with `startLoadingSteps`. */
  steps?: string[];
}

export function loadingHtml(options: LoadingOptions): string {
  const photo = hostImageUrl ? `<img class="bfc-avatar-photo" src="${hostImageUrl}" alt="" />` : "";
  const note = options.note ? `<p class="bfc-loading-note">${options.note}</p>` : "";
  const steps = options.steps?.length
    ? `<ul class="bfc-steps">${options.steps
        .map((text, i) => `<li class="bfc-step ${i === 0 ? "bfc-step--active" : "bfc-step--pending"}">${text}</li>`)
        .join("")}</ul>`
    : "";
  return `
    <div class="bfc-loading" role="status" aria-label="${options.label}">
      <div class="bfc-avatar" aria-hidden="true">
        <span class="bfc-avatar-ring"></span>
        <span class="bfc-avatar-disc">${photo}</span>
      </div>
      <p class="bfc-loading-heading">${options.heading}</p>
      ${note}
      ${steps}
    </div>
  `;
}

export interface LoadingSteps {
  /** Jump to a step (clamped), marking earlier ones done. */
  setStep(index: number): void;
  /** Jump to the last step. */
  finish(): void;
  stop(): void;
}

/** Walks the `.bfc-step` items inside `root` forward on a timer, sticking on the last one. The
 * steps are reassurance about roughly what is happening, not a measurement of progress: nothing
 * upstream reports which stage the ranking call is in, so callers with a real signal should
 * `setStep`/`finish` from it rather than wait for the timer. */
export function startLoadingSteps(root: HTMLElement, intervalMs: number): LoadingSteps {
  const items = Array.from(root.querySelectorAll<HTMLElement>(".bfc-step"));
  let current = 0;
  const setStep = (index: number) => {
    current = Math.max(0, Math.min(items.length - 1, index));
    items.forEach((item, i) => {
      item.classList.toggle("bfc-step--done", i < current);
      item.classList.toggle("bfc-step--active", i === current);
      item.classList.toggle("bfc-step--pending", i > current);
    });
  };
  const timer = items.length > 1 ? window.setInterval(() => setStep(current + 1), intervalMs) : undefined;
  const stop = () => {
    if (timer !== undefined) window.clearInterval(timer);
  };
  return {
    setStep,
    finish: () => setStep(items.length - 1),
    stop,
  };
}
